/**
 * Mock Stock Levels Data
 * 
 * Represents current stock quantities at various locations
 */

export interface StockLevel {
  stockCode: string;
  warehouseCode: string;
  locationCode: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number;
  minimumLevel: number;
  maximumLevel: number;
  reorderPoint: number;
  lastMovementDate: string;
  unitCost: number;
  totalValue: number;
}

export interface BatchLot {
  batchNumber: string;
  stockCode: string;
  warehouseCode: string;
  locationCode: string;
  quantity: number;
  manufactureDate: string;
  expiryDate?: string;
  supplierReference?: string;
  notes?: string;
}

export interface SerialNumber {
  serialNumber: string;
  stockCode: string;
  warehouseCode: string;
  locationCode: string;
  status: 'available' | 'reserved' | 'sold' | 'damaged';
  purchaseDate: string;
  warrantyExpiry?: string;
  notes?: string;
}

export interface LocationCapacity {
  warehouseCode: string;
  locationCode: string;
  maxCapacity: number;
  currentUtilization: number;
  unit: 'units' | 'pallets' | 'cubic_meters' | 'weight_kg';
}

/**
 * Mock stock levels across different locations
 */
export const mockStockLevels: StockLevel[] = [
  // Main Warehouse - Widget Type A
  {
    stockCode: 'WGT-A',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 150,
    quantityReserved: 20,
    quantityAvailable: 130,
    minimumLevel: 50,
    maximumLevel: 500,
    reorderPoint: 75,
    lastMovementDate: '2024-01-05',
    unitCost: 25.00,
    totalValue: 3750.00,
  },
  {
    stockCode: 'WGT-A',
    warehouseCode: 'WH-MAIN',
    locationCode: 'B2',
    quantityOnHand: 75,
    quantityReserved: 10,
    quantityAvailable: 65,
    minimumLevel: 30,
    maximumLevel: 200,
    reorderPoint: 50,
    lastMovementDate: '2024-01-03',
    unitCost: 25.00,
    totalValue: 1875.00,
  },
  
  // Secondary Warehouse - Widget Type A
  {
    stockCode: 'WGT-A',
    warehouseCode: 'WH-SEC',
    locationCode: 'A1',
    quantityOnHand: 45,
    quantityReserved: 5,
    quantityAvailable: 40,
    minimumLevel: 20,
    maximumLevel: 150,
    reorderPoint: 30,
    lastMovementDate: '2024-01-04',
    unitCost: 25.00,
    totalValue: 1125.00,
  },
  
  // Main Warehouse - Gadget Pro
  {
    stockCode: 'GDG-PRO',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 200,
    quantityReserved: 50,
    quantityAvailable: 150,
    minimumLevel: 100,
    maximumLevel: 1000,
    reorderPoint: 150,
    lastMovementDate: '2024-01-06',
    unitCost: 45.50,
    totalValue: 9100.00,
  },
  {
    stockCode: 'GDG-PRO',
    warehouseCode: 'WH-MAIN',
    locationCode: 'C3',
    quantityOnHand: 80,
    quantityReserved: 0,
    quantityAvailable: 80,
    minimumLevel: 50,
    maximumLevel: 300,
    reorderPoint: 75,
    lastMovementDate: '2024-01-02',
    unitCost: 45.50,
    totalValue: 3640.00,
  },
  
  // Secondary Warehouse - Gadget Pro
  {
    stockCode: 'GDG-PRO',
    warehouseCode: 'WH-SEC',
    locationCode: 'B2',
    quantityOnHand: 120,
    quantityReserved: 30,
    quantityAvailable: 90,
    minimumLevel: 60,
    maximumLevel: 400,
    reorderPoint: 100,
    lastMovementDate: '2024-01-05',
    unitCost: 45.50,
    totalValue: 5460.00,
  },
  
  // Main Warehouse - Component X
  {
    stockCode: 'CMP-X',
    warehouseCode: 'WH-MAIN',
    locationCode: 'B2',
    quantityOnHand: 500,
    quantityReserved: 100,
    quantityAvailable: 400,
    minimumLevel: 200,
    maximumLevel: 2000,
    reorderPoint: 300,
    lastMovementDate: '2024-01-07',
    unitCost: 12.75,
    totalValue: 6375.00,
  },
  
  // Regional Warehouse - Widget Type A
  {
    stockCode: 'WGT-A',
    warehouseCode: 'WH-REG',
    locationCode: 'A1',
    quantityOnHand: 30,
    quantityReserved: 0,
    quantityAvailable: 30,
    minimumLevel: 10,
    maximumLevel: 100,
    reorderPoint: 20,
    lastMovementDate: '2024-01-01',
    unitCost: 25.00,
    totalValue: 750.00,
  },
  
  // Main Warehouse - Widget Type B
  {
    stockCode: 'WGT-B',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 100,
    quantityReserved: 15,
    quantityAvailable: 85,
    minimumLevel: 40,
    maximumLevel: 400,
    reorderPoint: 60,
    lastMovementDate: '2024-01-05',
    unitCost: 18.00,
    totalValue: 1800.00,
  },
  {
    stockCode: 'WGT-B',
    warehouseCode: 'WH-MAIN',
    locationCode: 'B2',
    quantityOnHand: 60,
    quantityReserved: 10,
    quantityAvailable: 50,
    minimumLevel: 25,
    maximumLevel: 200,
    reorderPoint: 40,
    lastMovementDate: '2024-01-04',
    unitCost: 18.00,
    totalValue: 1080.00,
  },
  
  // Secondary Warehouse - Widget Type B
  {
    stockCode: 'WGT-B',
    warehouseCode: 'WH-SEC',
    locationCode: 'A1',
    quantityOnHand: 35,
    quantityReserved: 5,
    quantityAvailable: 30,
    minimumLevel: 15,
    maximumLevel: 120,
    reorderPoint: 25,
    lastMovementDate: '2024-01-03',
    unitCost: 18.00,
    totalValue: 630.00,
  },
  
  // Main Warehouse - Widget Type C
  {
    stockCode: 'WGT-C',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 80,
    quantityReserved: 10,
    quantityAvailable: 70,
    minimumLevel: 30,
    maximumLevel: 300,
    reorderPoint: 50,
    lastMovementDate: '2024-01-06',
    unitCost: 20.00,
    totalValue: 1600.00,
  },
  {
    stockCode: 'WGT-C',
    warehouseCode: 'WH-MAIN',
    locationCode: 'C3',
    quantityOnHand: 45,
    quantityReserved: 5,
    quantityAvailable: 40,
    minimumLevel: 20,
    maximumLevel: 150,
    reorderPoint: 30,
    lastMovementDate: '2024-01-02',
    unitCost: 20.00,
    totalValue: 900.00,
  },
  
  // Main Warehouse - Gadget Model X
  {
    stockCode: 'GDG-X',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 50,
    quantityReserved: 8,
    quantityAvailable: 42,
    minimumLevel: 20,
    maximumLevel: 200,
    reorderPoint: 30,
    lastMovementDate: '2024-01-07',
    unitCost: 90.00,
    totalValue: 4500.00,
  },
  {
    stockCode: 'GDG-X',
    warehouseCode: 'WH-MAIN',
    locationCode: 'B2',
    quantityOnHand: 30,
    quantityReserved: 5,
    quantityAvailable: 25,
    minimumLevel: 10,
    maximumLevel: 100,
    reorderPoint: 20,
    lastMovementDate: '2024-01-05',
    unitCost: 90.00,
    totalValue: 2700.00,
  },
  
  // Secondary Warehouse - Gadget Model X
  {
    stockCode: 'GDG-X',
    warehouseCode: 'WH-SEC',
    locationCode: 'B2',
    quantityOnHand: 25,
    quantityReserved: 3,
    quantityAvailable: 22,
    minimumLevel: 10,
    maximumLevel: 80,
    reorderPoint: 15,
    lastMovementDate: '2024-01-04',
    unitCost: 90.00,
    totalValue: 2250.00,
  },
  
  // Main Warehouse - Gadget Model Y
  {
    stockCode: 'GDG-Y',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 65,
    quantityReserved: 12,
    quantityAvailable: 53,
    minimumLevel: 25,
    maximumLevel: 250,
    reorderPoint: 40,
    lastMovementDate: '2024-01-08',
    unitCost: 105.00,
    totalValue: 6825.00,
  },
  {
    stockCode: 'GDG-Y',
    warehouseCode: 'WH-MAIN',
    locationCode: 'C3',
    quantityOnHand: 40,
    quantityReserved: 8,
    quantityAvailable: 32,
    minimumLevel: 15,
    maximumLevel: 120,
    reorderPoint: 25,
    lastMovementDate: '2024-01-06',
    unitCost: 105.00,
    totalValue: 4200.00,
  },
  
  // Secondary Warehouse - Gadget Model Y
  {
    stockCode: 'GDG-Y',
    warehouseCode: 'WH-SEC',
    locationCode: 'A1',
    quantityOnHand: 30,
    quantityReserved: 5,
    quantityAvailable: 25,
    minimumLevel: 12,
    maximumLevel: 100,
    reorderPoint: 20,
    lastMovementDate: '2024-01-05',
    unitCost: 105.00,
    totalValue: 3150.00,
  },
  
  // Main Warehouse - Gadget Model Z
  {
    stockCode: 'GDG-Z',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 45,
    quantityReserved: 10,
    quantityAvailable: 35,
    minimumLevel: 18,
    maximumLevel: 180,
    reorderPoint: 28,
    lastMovementDate: '2024-01-09',
    unitCost: 120.00,
    totalValue: 5400.00,
  },
  {
    stockCode: 'GDG-Z',
    warehouseCode: 'WH-MAIN',
    locationCode: 'B2',
    quantityOnHand: 28,
    quantityReserved: 6,
    quantityAvailable: 22,
    minimumLevel: 10,
    maximumLevel: 90,
    reorderPoint: 18,
    lastMovementDate: '2024-01-07',
    unitCost: 120.00,
    totalValue: 3360.00,
  },
  
  // Main Warehouse - Component 001
  {
    stockCode: 'CMP-001',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 500,
    quantityReserved: 80,
    quantityAvailable: 420,
    minimumLevel: 200,
    maximumLevel: 2000,
    reorderPoint: 300,
    lastMovementDate: '2024-01-08',
    unitCost: 3.00,
    totalValue: 1500.00,
  },
  {
    stockCode: 'CMP-001',
    warehouseCode: 'WH-MAIN',
    locationCode: 'B2',
    quantityOnHand: 300,
    quantityReserved: 50,
    quantityAvailable: 250,
    minimumLevel: 100,
    maximumLevel: 1000,
    reorderPoint: 200,
    lastMovementDate: '2024-01-06',
    unitCost: 3.00,
    totalValue: 900.00,
  },
  
  // Main Warehouse - Component 002
  {
    stockCode: 'CMP-002',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 400,
    quantityReserved: 60,
    quantityAvailable: 340,
    minimumLevel: 150,
    maximumLevel: 1500,
    reorderPoint: 250,
    lastMovementDate: '2024-01-07',
    unitCost: 4.50,
    totalValue: 1800.00,
  },
  {
    stockCode: 'CMP-002',
    warehouseCode: 'WH-MAIN',
    locationCode: 'C3',
    quantityOnHand: 250,
    quantityReserved: 40,
    quantityAvailable: 210,
    minimumLevel: 80,
    maximumLevel: 800,
    reorderPoint: 150,
    lastMovementDate: '2024-01-05',
    unitCost: 4.50,
    totalValue: 1125.00,
  },
  
  // Main Warehouse - Component 003
  {
    stockCode: 'CMP-003',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 350,
    quantityReserved: 55,
    quantityAvailable: 295,
    minimumLevel: 120,
    maximumLevel: 1200,
    reorderPoint: 220,
    lastMovementDate: '2024-01-09',
    unitCost: 6.00,
    totalValue: 2100.00,
  },
  {
    stockCode: 'CMP-003',
    warehouseCode: 'WH-MAIN',
    locationCode: 'B2',
    quantityOnHand: 200,
    quantityReserved: 35,
    quantityAvailable: 165,
    minimumLevel: 70,
    maximumLevel: 700,
    reorderPoint: 130,
    lastMovementDate: '2024-01-07',
    unitCost: 6.00,
    totalValue: 1200.00,
  },
  
  // Main Warehouse - Assembly 100
  {
    stockCode: 'ASM-100',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantityOnHand: 25,
    quantityReserved: 5,
    quantityAvailable: 20,
    minimumLevel: 10,
    maximumLevel: 100,
    reorderPoint: 15,
    lastMovementDate: '2024-01-10',
    unitCost: 150.00,
    totalValue: 3750.00,
  },
  {
    stockCode: 'ASM-100',
    warehouseCode: 'WH-MAIN',
    locationCode: 'C3',
    quantityOnHand: 18,
    quantityReserved: 3,
    quantityAvailable: 15,
    minimumLevel: 8,
    maximumLevel: 60,
    reorderPoint: 12,
    lastMovementDate: '2024-01-08',
    unitCost: 150.00,
    totalValue: 2700.00,
  },
  
  // Secondary Warehouse - Assembly 100
  {
    stockCode: 'ASM-100',
    warehouseCode: 'WH-SEC',
    locationCode: 'A1',
    quantityOnHand: 12,
    quantityReserved: 2,
    quantityAvailable: 10,
    minimumLevel: 5,
    maximumLevel: 40,
    reorderPoint: 8,
    lastMovementDate: '2024-01-06',
    unitCost: 150.00,
    totalValue: 1800.00,
  },
];

/**
 * Mock batch/lot tracking data
 */
export const mockBatchLots: BatchLot[] = [
  {
    batchNumber: 'BATCH-2024-001',
    stockCode: 'WGT-A',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantity: 100,
    manufactureDate: '2024-01-01',
    expiryDate: '2025-01-01',
    supplierReference: 'PO-2024-001',
    notes: 'First batch of 2024',
  },
  {
    batchNumber: 'BATCH-2024-002',
    stockCode: 'WGT-A',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantity: 50,
    manufactureDate: '2024-01-15',
    expiryDate: '2025-01-15',
    supplierReference: 'PO-2024-015',
  },
  {
    batchNumber: 'BATCH-2024-003',
    stockCode: 'GDG-PRO',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    quantity: 150,
    manufactureDate: '2024-01-10',
    supplierReference: 'PO-2024-010',
  },
];

/**
 * Mock serial number tracking data
 */
export const mockSerialNumbers: SerialNumber[] = [
  {
    serialNumber: 'SN-WGT-A-001',
    stockCode: 'WGT-A',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    status: 'available',
    purchaseDate: '2024-01-01',
    warrantyExpiry: '2025-01-01',
  },
  {
    serialNumber: 'SN-WGT-A-002',
    stockCode: 'WGT-A',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    status: 'reserved',
    purchaseDate: '2024-01-01',
    warrantyExpiry: '2025-01-01',
  },
  {
    serialNumber: 'SN-GDG-PRO-001',
    stockCode: 'GDG-PRO',
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    status: 'available',
    purchaseDate: '2024-01-05',
    warrantyExpiry: '2026-01-05',
  },
];

/**
 * Mock location capacity data
 */
export const mockLocationCapacities: LocationCapacity[] = [
  {
    warehouseCode: 'WH-MAIN',
    locationCode: 'A1',
    maxCapacity: 1000,
    currentUtilization: 350,
    unit: 'units',
  },
  {
    warehouseCode: 'WH-MAIN',
    locationCode: 'B2',
    maxCapacity: 500,
    currentUtilization: 575,
    unit: 'units',
  },
  {
    warehouseCode: 'WH-SEC',
    locationCode: 'A1',
    maxCapacity: 300,
    currentUtilization: 165,
    unit: 'units',
  },
];

/**
 * Helper function to get stock level for a specific location
 */
export const getStockLevel = (
  stockCode: string,
  warehouseCode: string,
  locationCode: string
): StockLevel | undefined => {
  return mockStockLevels.find(
    (level) =>
      level.stockCode === stockCode &&
      level.warehouseCode === warehouseCode &&
      level.locationCode === locationCode
  );
};

/**
 * Helper function to get all stock levels for a stock item
 */
export const getStockLevelsByItem = (stockCode: string): StockLevel[] => {
  return mockStockLevels.filter((level) => level.stockCode === stockCode);
};

/**
 * Helper function to get total available stock for an item
 */
export const getTotalAvailableStock = (stockCode: string): number => {
  return mockStockLevels
    .filter((level) => level.stockCode === stockCode)
    .reduce((total, level) => total + level.quantityAvailable, 0);
};

/**
 * Helper function to get batch/lot numbers for a location
 */
export const getBatchLotsForLocation = (
  stockCode: string,
  warehouseCode: string,
  locationCode: string
): BatchLot[] => {
  return mockBatchLots.filter(
    (batch) =>
      batch.stockCode === stockCode &&
      batch.warehouseCode === warehouseCode &&
      batch.locationCode === locationCode
  );
};

/**
 * Helper function to get serial numbers for a location
 */
export const getSerialNumbersForLocation = (
  stockCode: string,
  warehouseCode: string,
  locationCode: string,
  status?: SerialNumber['status']
): SerialNumber[] => {
  let serials = mockSerialNumbers.filter(
    (serial) =>
      serial.stockCode === stockCode &&
      serial.warehouseCode === warehouseCode &&
      serial.locationCode === locationCode
  );
  
  if (status) {
    serials = serials.filter((serial) => serial.status === status);
  }
  
  return serials;
};

/**
 * Helper function to get location capacity
 */
export const getLocationCapacity = (
  warehouseCode: string,
  locationCode: string
): LocationCapacity | undefined => {
  return mockLocationCapacities.find(
    (capacity) =>
      capacity.warehouseCode === warehouseCode &&
      capacity.locationCode === locationCode
  );
};

/**
 * Helper function to check if location has capacity for additional stock
 */
export const hasCapacityForQuantity = (
  warehouseCode: string,
  locationCode: string,
  additionalQuantity: number
): { hasCapacity: boolean; availableCapacity: number } => {
  const capacity = getLocationCapacity(warehouseCode, locationCode);
  
  if (!capacity) {
    return { hasCapacity: true, availableCapacity: Infinity }; // No limit if not defined
  }
  
  const availableCapacity = capacity.maxCapacity - capacity.currentUtilization;
  const hasCapacity = availableCapacity >= additionalQuantity;
  
  return { hasCapacity, availableCapacity };
};
